<template>
  <ProductListPagination
    v-if="totalPages && totalPages > 1"
    class="product-list-pagination"
  >
    <nav class="product-list-pagination__nav">
      <ul class="product-list-pagination__list">
        <li
          class="product-list-pagination__list-item product-list-pagination__list-item--prev"
          :class="{
            'product-list-pagination__list-item--disabled': currentPage <= 1
          }"
        >
          <a
            href="javascript:"
            class="product-list-pagination__link product-list-pagination__link--prev"
            :aria-disabled="currentPage <= 1"
            @click.prevent="handlePageChange(currentPage - 1)"
          >
            <span class="visually-hidden">{{ prevLabel }}</span>
          </a>
        </li>
        <li
          v-if="currentPage - 2 >= 1"
          class="product-list-pagination__list-item"
        >
          <a
            href="javascript:"
            class="product-list-pagination__link"
            @click.prevent="handlePageChange(currentPage - 2)"
          >
            {{ currentPage - 2 }}
          </a>
        </li>
        <li
          v-if="currentPage - 1 >= 1"
          class="product-list-pagination__list-item"
        >
          <a
            href="javascript:"
            class="product-list-pagination__link"
            @click.prevent="handlePageChange(currentPage - 1)"
          >
            {{ currentPage - 1 }}
          </a>
        </li>
        <li
          class="product-list-pagination__list-item product-list-pagination__list-item--active"
        >
          <a
            href="javascript:"
            class="product-list-pagination__link product-list-pagination__link--active"
            aria-current="page"
          >
            {{ currentPage }}
          </a>
        </li>
        <li class="product-list-pagination__list-item">
          <a
            v-if="currentPage + 1 <= totalPages"
            href="javascript:"
            class="product-list-pagination__link"
            @click.prevent="handlePageChange(currentPage + 1)"
          >
            {{ currentPage + 1 }}
          </a>
        </li>
        <li class="product-list-pagination__list-item">
          <a
            v-if="totalPages && currentPage + 2 <= totalPages"
            href="javascript:"
            class="product-list-pagination__link"
            @click.prevent="handlePageChange(currentPage + 2)"
          >
            {{ currentPage + 2 }}
          </a>
        </li>
        <li
          class="product-list-pagination__list-item product-list-pagination__list-item--next"
          :class="{
            'product-list-pagination__list-item--disabled':
              currentPage >= totalPages
          }"
        >
          <a
            href="javascript:"
            class="product-list-pagination__link product-list-pagination__link--next"
            :aria-disabled="currentPage >= totalPages"
            @click.prevent="handlePageChange(currentPage + 1)"
          >
            <span class="visually-hidden">{{ nextLabel }}</span>
          </a>
        </li>
      </ul>
    </nav>
  </ProductListPagination>
</template>

<script setup lang="ts">
import { ProductListPagination } from '@hypercodestudio/basler-components';

interface Props {
  /**
   * Current page number (1-based)
   */
  currentPage: number;

  /**
   * Total number of pages
   */
  totalPages: number;

  /**
   * Label for the previous button (for accessibility)
   * @default "Previous page"
   */
  prevLabel?: string;

  /**
   * Label for the next button (for accessibility)
   * @default "Next page"
   */
  nextLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  prevLabel: 'Previous page',
  nextLabel: 'Next page'
});

const emit = defineEmits<{
  /**
   * Emitted when a page link is clicked
   * @param e The name of the event
   * @param page The page number that was clicked
   */
  (e: 'page-change', page: number): void;
}>();

/**
 * Handle page change and emit the event
 */
function handlePageChange(page: number) {
  // Don't allow navigation to invalid pages
  if (page < 1 || page > props.totalPages) {
    return;
  }

  emit('page-change', page);
}
</script>

<style scoped>
.product-list-pagination__list-item--disabled {
  opacity: 0.5;
  pointer-events: none;
}

.product-list-pagination__link[aria-disabled='true'] {
  cursor: not-allowed;
  opacity: 0.5;
}
</style>
